import { Model } from "@api/apiClient";
import type { INodeDto } from "@/types/INodeDto";
import { Scene } from "./Scene";
import { GroupingField, LabelResolver, TreeBuilder, TreeBuilderOptions, TreeRow } from "./TreeBuilder";

/**
 * SceneManager provides methods for managing Scene, tree building, and node operations.
 * This class is designed to be used as a singleton property in AppStore.
 */
export class SceneManager {
  /** The Scene containing all loaded models and their node (INodeDto) data */
  public scene: Scene = new Scene();

  /** The flattened tree rows for the grid */
  public treeRows: TreeRow[] = [];

  /** Defines how nodes are grouped in the tree */
  public groupingFields: GroupingField[] = TreeBuilder.defaultGroupingFields();

  /** Version counter when scene or tree data changes (tree rebuilds, setTreeRows, populateSceneFromModelsAndNodes, clear). */
  public treeVersion: number = 0;

  /** Called whenever treeVersion is incremented. Used to notify React (e.g. global state) so consumers re-render. */
  private _onTreeVersionChange?: () => void;

  /** Optional function to resolve field values to display labels */
  public labelResolver?: LabelResolver;

  /** Set a callback to run whenever treeVersion is incremented (e.g. bump global sceneTreeVersion for React). */
  setOnTreeVersionChange(callback: (() => void) | undefined): void {
    this._onTreeVersionChange = callback;
  }

  private bumpTreeVersion(): void {
    this.treeVersion++;
    this._onTreeVersionChange?.();
  }

  /** 
   * Tree builder options controlling grouping behavior.
   * preserveHierarchy (default: true): Only group Element nodes, keeping 
   * Component/Supplemental nodes as children of their parents.
   */
  public treeBuilderOptions: TreeBuilderOptions = { preserveHierarchy: true };

  /**
   * Sets the label resolver function for resolving field values to display labels.
   */
  setLabelResolver(resolver: LabelResolver | undefined): void {
    this.labelResolver = resolver;
  }

  /**
   * Resets the manager to initial state with a fresh Scene.
   */
  reset(): void {
    this.scene.clear();
    this.scene = new Scene();
    this.treeRows = [];
    this.treeVersion = 0;
    this.groupingFields = TreeBuilder.defaultGroupingFields();
    this.treeBuilderOptions = { preserveHierarchy: true };
    // Note: labelResolver is intentionally NOT reset - it should persist
  }

  /**
   * Clears all data but keeps the same Scene instance.
   */
  clear(): void {
    this.scene.clear();
    this.treeRows = [];
    this.bumpTreeVersion();
  }

  /**
   * Sets new grouping fields for tree building.
   */
  setGroupingFields(fields: GroupingField[]): void {
    this.groupingFields = fields;
  }

  /**
   * Adds a model with its nodes to the scene and builds the tree.
   * @param model The Model metadata
   * @param nodes The node array (INodeDto) for this model
   * @returns The tree rows after rebuilding
   */
  addModelWithNodes(model: Model, nodes: INodeDto[]): TreeRow[] {
    if (!this.scene.hasModel(model.modelId!)) {
      this.scene.addModelWithNodes(model, nodes);
    }
    return this.rebuildTree();
  }

  /**
   * Populates the scene from model metadata and nodes by modelId (e.g. from useMultiScenarioNodeData).
   * Use this from useProjectAnalysisData when node data is ready so scene.getModel(modelId) has nodes for analysis.
   */
  populateSceneFromModelsAndNodes(
    models: Model[],
    nodesByModelId: Record<string, INodeDto[] | null>
  ): void {
    console.log("[SceneManager] populateSceneFromModelsAndNodes: ", models, nodesByModelId);

    this.scene.clear();
    for (const model of models) {
      const modelId = model.modelId!;
      const nodes = nodesByModelId[modelId] ?? [];
      if (nodes.length > 0) {
        this.scene.addModelWithNodes(model, nodes);
      }
    }
    this.bumpTreeVersion();
  }

  /**
   * Removes a model and its nodes from the scene.
   * @param modelId The model ID to remove
   * @returns true if the model was removed
   */
  removeModel(modelId: string): boolean {
    const removed = this.scene.removeModel(modelId);
    if (removed) {
      this.rebuildTree();
    }
    return removed;
  }

  /**
   * Checks if a model exists in the scene.
   */
  hasModel(modelId: string): boolean {
    return this.scene.hasModel(modelId);
  }

  /**
   * Sets the tree builder options.
   */
  setTreeBuilderOptions(options: TreeBuilderOptions): void {
    this.treeBuilderOptions = { ...this.treeBuilderOptions, ...options };
  }

  /**
   * Rebuilds the tree from the current Scene using the current grouping fields.
   * @returns The new tree rows
   */
  rebuildTree(): TreeRow[] {
    if (this.scene.getTotalNodeCount() === 0) {
      this.treeRows = [];
      this.bumpTreeVersion();
      return this.treeRows;
    }

    const treeBuilder = new TreeBuilder(
      this.scene.getNodes(),
      this.groupingFields,
      this.labelResolver,
      undefined, // onProgress
      this.treeBuilderOptions
    );
    this.treeRows = treeBuilder.build();
    this.bumpTreeVersion();
    return this.treeRows;
  }

  /**
   * Gets a node from the scene by its nodeId.
   * Searches across all models in the scene.
   */
  getNodeByNodeId(nodeId: bigint): INodeDto | null {
    for (const model of this.scene.models) {
      const node = model.getNodeByNodeId(nodeId);
      if (node) return node;
    }
    return null;
  }

  /**
   * Gets a tree row by its id.
   */
  getTreeRowById(id: string): TreeRow | undefined {
    return this.treeRows.find(row => row.id === id);
  }

  /**
   * Gets all nodes from the scene.
   */
  getNodes(): readonly INodeDto[] {
    return this.scene.getNodes();
  }

  /**
   * Gets the total node count across all models.
   */
  getTotalNodeCount(): number {
    return this.scene.getTotalNodeCount();
  }

  /**
   * Gets the number of models in the scene.
   */
  getModelCount(): number {
    return this.scene.getModelCount();
  }

  /**
   * Gets all unique model IDs in the scene.
   */
  getModelIds(): string[] {
    return this.scene.getUniqueModelIds();
  }

  /**
   * Sets tree rows (for clearing only). Tree rows are normally derived from the scene
   * via rebuildTree(); the scene (InstanceModels / node array) is the single source of truth.
   */
  setTreeRows(rows: TreeRow[]): void {
    this.treeRows = rows || [];
    this.bumpTreeVersion();
  }
}

